import { useEffect, useState, useCallback, useRef } from "react";
import { getPaginatedIssueClassifications } from "../services/classificationService";

const useFetchPaginatedIssueClassifications = ({
  setIsLoading,
  page,
  pSize,
  searchTerm,
  filterIds,
  isActive,
}: {
  setIsLoading: (loading: boolean) => void;
  page: number;
  pSize: number;
  searchTerm?: string;
  filterIds?: string;
  isActive?: boolean;
}) => {
  const [classificationsData, setClassificationsData] = useState<any>();
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchClassifications = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      try {
        setIsLoading(true);
        const result = await getPaginatedIssueClassifications(
          page,
          pSize,
          searchQuery || searchTerm,
          abortControllerRef.current.signal,
          filterIds,
          isActive
        );
        setClassificationsData(result);
      } catch (error: any) {
        console.error("Error fetching paginated issue classifications:", error);
        // Don't update state if request was aborted
        if (error.name !== "AbortError") {
          setClassificationsData(null);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, page, pSize, searchTerm, filterIds, isActive]
  );

  useEffect(() => {
    fetchClassifications();
  }, [fetchClassifications]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [classificationsData, fetchClassifications];
};

export default useFetchPaginatedIssueClassifications;
